import React, { useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import FinancialAidModal from "../components/FinancialAidModal";
import AllCoursesViewer from "../components/AllCoursesViewer";
import RegistrationDialogModal from "../components/RegistrationDialogModal";
import { FaShoppingCart } from "react-icons/fa";
import NotificationContext from "../context/NotificationContext";
import { useViewPermission } from "../context/PermissionContext";
import Loader from "../components/Loader";
import { useMutationLock } from "../hooks/useMutationLock";
import * as usersApi from "../api/users";
import * as registrationsApi from "../api/registrations";
import * as publicApi from "../api/publicApi";
import * as financialAidApi from "../api/financialAid";

export default function RegisterCourses() {
  const { user, isAuthenticated, loginWithPopup } = useAuth0();
  const notificationCtx = useContext(NotificationContext);
  const { hasPermission, loading: permLoading } = useViewPermission("view_register_courses");
  const { busy: registering, run: runRegister } = useMutationLock();

  const [mycourses, setMyCourses] = useState([]);
  const [courses, setCourses] = useState([]);

  const [selectedCourses, setSelectedCourses] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showFinancialAidModal, setShowFinancialAidModal] = useState(false);

  const [userId, setUserId] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [feemode, setFeeMode] = useState("");

  const [showMobileCart, setShowMobileCart] = useState(false);
  const [selectedfeemode, setselectedfeemode] = useState("");
  const [allgrades, setallgrades] = useState([]);

  useEffect(() => {
    if (permLoading || !hasPermission || !user?.email) return;

    setEmail(user.email);

    let cancelled = false;
    const fetchUserData = async () => {
      try {
        const userRes = await usersApi.lookup({ email: user.email });

        const { name, phoneNumber, id: lookedUpUserId } = userRes.data;
        if (cancelled) return;
        setName(name);
        setPhone(phoneNumber);
        setUserId(lookedUpUserId);

        const myCoursesRes = await registrationsApi.myCourses();
        if (cancelled) return;
        setMyCourses(myCoursesRes.data);
        setselectedfeemode(myCoursesRes.data[0]?.feemode || "");
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchUserData();
    return () => {
      cancelled = true;
    };
  }, [user, hasPermission, permLoading]);

  // Public catalog — available without login
  useEffect(() => {
    const fetchAllCourses = async () => {
      try {
        const coursesRes = await publicApi.getActiveCourses();
        setCourses(coursesRes.data);
        const response = await publicApi.getGrades();
        setallgrades(response.data);
      } catch (error) {
        console.error("Error fetching catalog:", error);
      }
    };

    fetchAllCourses();
  }, []);

  async function getCityFromCoordinates(latitude, longitude) {
    const apiKey = process.env.REACT_APP_GEOCODING_API;

    const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;

    try {
      const response = await fetch(url);
      const data = await response.json();
      // console.log(data);
      if (data.status === "OK" && data.results.length > 0) {
        const city = data.results[0].address_components.find((component) =>
          component.types.includes("locality"),
        )?.long_name;
        return city || "Unknown";
      } else {
        console.error("City not found or API error:", data.status);
        return "Unknown";
      }
    } catch (error) {
      console.error("Error fetching city data:", error);
      return "Unknown";
    }
  }
  const [coords, setCoords] = useState(null);
  useEffect(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error fetching location:", error);
        },
        {
          enableHighAccuracy: true, // Request GPS-level accuracy
          timeout: 10000, // Wait up to 10 seconds
          maximumAge: 0, // Do not use cached location
        },
      );
    } else {
      console.error("Geolocation is not available in this browser.");
    }
  }, []);

  async function registerCourse() {
    if (!feemode)
      return notificationCtx.show({
        message: "Please select a fee mode",
      });

    await runRegister(async () => {
      const registrationData = {
        email,
        userId,
        name,
        phone,
        feemode,
        courses: selectedCourses,
      };

      try {
        let city = "Unknown";

        const latitude = coords.latitude;
        const longitude = coords.longitude;

        city = await getCityFromCoordinates(latitude, longitude);

        const updatedDetails = { ...registrationData, city };
        const result = await registrationsApi.create(updatedDetails);

        if (result.data) {
          notificationCtx.show({
            message: "Course Registration Successful",
            onConfirm: () => (window.location.href = "/checkout"),
            stat: "success",
          });
        } else {
          notificationCtx.show({
            message: "Course Registration Failed",
            stat: "error",
          });
        }
      } catch (error) {
        console.error("Error during registration:", error);
        notificationCtx.show({
          message:
            "An error occurred during registration. Please try again later.",
          stat: "error",
        });
      }
    });
  }

  async function checkFinancialAidRecord() {
    if (registering) return;
    try {
      const { data } = await financialAidApi.checkRecord({ email });
      if (!data.valid) setShowFinancialAidModal(true);
      else registerCourse();
    } catch (error) {
      console.error("Error checking financial aid record:", error);
    }
  }

  const handleDetailsSaved = () => {
    setShowFinancialAidModal(false);
    registerCourse();
  };

  const handleSelectCourse = (c) => {
    const temp = [...selectedCourses];
    const course = {
      ...c,
      status: "pending",
    };
    temp.push(course);
    setSelectedCourses(temp);
  };
  const handleDeselectCourse = (c) => {
    const temp = selectedCourses.filter((course) => course.id !== c.id);
    setSelectedCourses(temp);
  };

  const [selectedCourseIds, setSelectedCourseIds] = useState([]);

  const selectCourse = (course) => () => {
    setSelectedCourseIds((prev) => {
      const isSelected = prev.includes(course.id);

      if (isSelected) {
        // Deselect
        handleDeselectCourse(course);
        return prev.filter((id) => id !== course.id);
      } else {
        // Select
        handleSelectCourse(course);
        return [...prev, course.id];
      }
    });
  };

  useEffect(() => {
    if (!user && !mycourses) return;

    const myCoursesRes = mycourses.flatMap((course) => {
      return course.courses
        .filter((c) => c.status !== "pending" || c.status !== "enrolled")
        .map((c) => c.id);
    });

    setSelectedCourses((prev) => {
      const filtered = prev.filter(
        (course) => !myCoursesRes[0].includes(course.id),
      );
      const unchanged =
        filtered.length === prev.length &&
        filtered.every((course, index) => course.id === prev[index]?.id);
      return unchanged ? prev : filtered;
    });
  }, [mycourses, user]);

  useEffect(() => {
    setSelectedCourseIds(selectedCourses.map((course) => course.id));
  }, [selectedCourses]);

  const [suggestions, setSuggestions] = useState([]);

  useEffect(() => {
    // Extract grade names from selectedCourses based on allgrades
    const selectedGrades = allgrades.filter((grade) =>
      selectedCourses.some((course) =>
        course.title.toLowerCase().includes(grade.name.toLowerCase()),
      ),
    );

    // Get registered course IDs (status = enrolled or pending)
    const registeredCourseIds =
      mycourses?.flatMap((myCourse) =>
        myCourse.courses
          .filter((c) => c.status === "pending" || c.status === "enrolled")
          .map((c) => c.id),
      ) || [];

    // Filter suggestions
    const suggestion = courses.filter((course) => {
      // Skip already selected courses
      const alreadySelected = selectedCourses.some(
        (selectedCourse) => selectedCourse.id === course.id,
      );
      if (alreadySelected) return false;

      // Skip already registered courses
      const isRegistered = registeredCourseIds.includes(course.id);
      if (isRegistered) return false;

      // Check if course matches any of the selected grades
      const belongsToSelectedGrade = selectedGrades.some((grade) =>
        course.title.toLowerCase().includes(grade.name.toLowerCase()),
      );

      return belongsToSelectedGrade;
    });

    setSuggestions(suggestion);
  }, [selectedCourses, mycourses, courses, allgrades]);

  const [selectedgrade, setselectedgrade] = useState("");

  if (isAuthenticated && permLoading) {
    return (
      <div className="text-center">
        <Navbar />
        <div style={{ paddingTop: 120 }}>
          <Loader />
        </div>
      </div>
    );
  }

  const promptRegisterOrLogin = () => {
    if (!isAuthenticated) {
      loginWithPopup();
      return;
    }
    if (!hasPermission) {
      notificationCtx.show({
        message: "You do not have permission to register for courses.",
      });
      return;
    }
    if (selectedCourses.length === 0) {
      notificationCtx.show({
        message: "Please select a course to register",
      });
      return;
    }
    setShowForm(true);
  };

  return (
    <div className="text-center">
        <Navbar />
        <br />
        <div className="row">
          <div className="col-md-9" style={{ paddingRight: 0 }}>
            <div
              className="mb-5"
              style={{
                backgroundColor: "#f2ebe3",
                borderRadius: "25px",
                padding: "20px",
              }}
            >
              <div className="d-flex justify-content-between align-items-center px-5 pt-3">
                <h1 style={{ fontWeight: "700", fontSize: "48px" }}>
                  AVAILABLE COURSES
                </h1>
                <button
                  className="btn btn-primary d-none d-md-block"
                  style={{
                    display: "block",
                    backgroundColor: "#d5d1fd",
                    borderColor: "#d5d1fd",
                    padding: "15px 30px",
                  }}
                  onClick={promptRegisterOrLogin}
                >
                  Register Courses
                </button>
              </div>
              {courses.length === 0 ? (
                <p className="text-center">No courses available</p>
              ) : (
                <div className="container">
                  {allgrades.length > 0 && selectedgrade === "" ? (
                    <div
                      className="modal fade show"
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0,0,0,0.9)",
                        zIndex: 10500,
                      }}
                      tabIndex="-1"
                    >
                      <div
                        className="modal-dialog modal-lg modal-dialog-centered"
                        style={{
                          width: "100%",
                          margin: "auto",
                        }}
                      >
                        <div
                          className="modal-content"
                          style={{
                            backgroundColor: "#ffffff",
                            padding: "0 20px",
                            borderRadius: "16px",
                            boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                          }}
                        >
                          <div
                            className="modal-body"
                            style={{
                              overflowY: "auto",
                              maxHeight: "80vh",
                              padding: "40px 20px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <h1
                              className="text-center blink-text mb-5"
                              style={{ fontWeight: "700" }}
                            >
                              REGISTER NEW UPCOMING COURSES FROM THE FOLLOWING
                            </h1>
                            <h5>Select an option to continue</h5>
                            <div className="row justify-content-center mt-3">
                              {allgrades?.map((grade) => (
                                <div
                                  key={grade.grade}
                                  className="col-md-4 mb-4 raise-on-hover"
                                  onClick={() => {
                                    setselectedgrade(grade.name);
                                    const sortedGrades = [
                                      grade,
                                      ...allgrades.filter(
                                        (g) => g.name !== grade.name,
                                      ),
                                    ];
                                    setallgrades(sortedGrades);
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    border:
                                      selectedgrade === grade.name
                                        ? "2px solid #d5d1fd"
                                        : "2px solid transparent",
                                    borderRadius: "10px",
                                    padding: "10px",
                                    textAlign: "center",
                                  }}
                                >
                                  <h4>{grade.name}</h4>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {allgrades?.map((grade) => (
                    <div key={grade.grade} className="mb-4">
                      <h2 className="text-center">{grade.name}</h2>
                      <div className="row">
                        <AllCoursesViewer
                          mycourses={mycourses}
                          courses={courses}
                          selectCourse={selectCourse}
                          selectedCourseIds={selectedCourseIds}
                          isLogin={isAuthenticated}
                          loginWithPopup={loginWithPopup}
                          filterKey={grade.name}
                        />
                      </div>
                    </div>
                  ))}

                  {showForm && (
                    <RegistrationDialogModal
                      setShowForm={setShowForm}
                      courses={selectedCourses}
                      mycourses={mycourses}
                      selectCourse={selectCourse}
                      selectedCourseIds={selectedCourseIds}
                      suggestions={suggestions}
                      userId={userId}
                      name={name}
                      phone={phone}
                      feemode={feemode}
                      setfeemode={setFeeMode}
                      checkFinancialAidRecord={checkFinancialAidRecord}
                      registerCourse={registerCourse}
                      registering={registering}
                      selectedfeemode={selectedfeemode}
                    />
                  )}

                  {showFinancialAidModal && (
                    <FinancialAidModal
                      userEmail={email}
                      id={userId}
                      onDetailsSaved={handleDetailsSaved}
                      onClose={() => setShowFinancialAidModal(false)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Desktop Cart (fixed on right for md and up) */}
          <div
            className="d-none d-md-block"
            style={{
              position: "fixed",
              top: "150px",
              right: "30px",
              width: "340px",
              zIndex: 1040,
            }}
          >
            <div
              className="mb-5"
              style={{
                backgroundColor: "#f2ebe3",
                borderRadius: "25px",
                padding: "20px",
                width: "100%",
                height: "60vh",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                overflowY: "auto",
              }}
            >
              <h1 style={{ fontWeight: "600", fontSize: "30px" }}>Cart</h1>
              {selectedCourses.length === 0 ? (
                <h6 className="text-center">
                  Your cart is empty. No course selected!
                </h6>
              ) : (
                <>
                  <div className="mb-3">
                    <h5>Selected Courses: </h5>
                    <ol>
                      {selectedCourses.map((course) => (
                        <div
                          key={course.id}
                          className="d-flex justify-content-between mb-2"
                        >
                          <li>{course.title}</li>
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={selectCourse(course)}
                            style={{
                              marginLeft: "10px",
                              backgroundColor: "#f2c4c4",
                              borderColor: "#f2c4c4",
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </ol>
                  </div>
                  {suggestions.length > 0 && (
                    <h5 className="my-3">Popular with your courses</h5>
                  )}
                  <div
                    style={{
                      maxHeight: "200px", // Adjust height as needed
                      overflowY: "auto",
                      marginBottom: "16px",
                      paddingRight: "4px",
                    }}
                  >
                    <Suggestions
                      suggestions={suggestions}
                      selectCourse={selectCourse}
                      selectedCourseIds={selectedCourseIds}
                    />
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{
                      display: "block",
                      backgroundColor: "#d5d1fd",
                      borderColor: "#d5d1fd",
                      padding: "15px 30px",
                      position: "absolute",
                      bottom: "80px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "80%",
                    }}
                    onClick={promptRegisterOrLogin}
                  >
                    Proceed to Checkout
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile Cart Icon (bottom left) */}
          <div
            className="d-md-none"
            style={{
              position: "fixed",
              bottom: "20px",
              left: "20px",
              zIndex: 20000,
            }}
          >
            <button
              className="btn btn-primary rounded-circle"
              style={{
                width: "60px",
                height: "60px",
                backgroundColor: "#d5d1fd",
                borderColor: "#d5d1fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "28px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                position: "fixed",
                zIndex: 20000,
                left: "20px",
                bottom: "20px",
              }}
              onClick={() => setShowMobileCart(true)}
            >
              <FaShoppingCart />
              {selectedCourses.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    background: "#f44336",
                    color: "#fff",
                    borderRadius: "50%",
                    width: "22px",
                    height: "22px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "14px",
                    fontWeight: "bold",
                  }}
                >
                  {selectedCourses.length}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Cart Modal */}
          {showMobileCart && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} // Added background color
              tabIndex="-1"
            >
              <div
                className="modal-dialog modal-lg modal-dialog-centered"
                style={{
                  maxHeight: "70vh", // Ensures modal doesn't exceed screen height
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  className="modal-content"
                  style={{
                    flexGrow: 1,
                    backgroundColor: "#f2ebe3",
                    borderRadius: "15px",
                    padding: "0 20px",
                  }}
                >
                  <div className="modal-header">
                    <h1 style={{ fontWeight: "600", fontSize: "30px" }}>
                      Cart
                    </h1>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowMobileCart(false)}
                    ></button>
                  </div>
                  <div
                    className="modal-body"
                    style={{
                      overflowY: "auto", // Enables scrolling only for modal body
                      maxHeight: "50vh", // Limits body height, adjust if needed
                    }}
                  >
                    {selectedCourses.length === 0 ? (
                      <h6 className="text-center">
                        Your cart is empty. No course selected!
                      </h6>
                    ) : (
                      <>
                        <div className="mb-3">
                          <h5>Selected Courses: </h5>
                          <ol>
                            {selectedCourses.map((course) => (
                              <div key={course.id}>
                                <li>{course.title}</li>
                              </div>
                            ))}
                          </ol>
                        </div>
                        {suggestions.length > 0 && (
                          <h5 className="my-3">Popular with your courses</h5>
                        )}
                        <div
                          style={{
                            maxHeight: "200px", // Adjust height as needed
                            overflowY: "auto",
                            marginBottom: "16px",
                            paddingRight: "4px",
                          }}
                        >
                          <Suggestions
                            suggestions={suggestions}
                            selectCourse={selectCourse}
                            selectedCourseIds={selectedCourseIds}
                          />
                        </div>
                        <button
                          className="btn btn-primary"
                          style={{
                            display: "block",
                            backgroundColor: "#d5d1fd",
                            borderColor: "#d5d1fd",
                            padding: "15px 30px",
                          }}
                          onClick={() => {
                            setShowMobileCart(false);
                            promptRegisterOrLogin();
                          }}
                        >
                          Proceed to Checkout
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
  );
}

function Suggestions(props) {
  return (
    <div>
      <ol>
        {props.suggestions.map((course) => {
          return (
            <div
              key={course.id}
              className="d-flex justify-content-between mb-2"
            >
              <li>{course.title}</li>
              <button
                className="btn btn-primary"
                onClick={props.selectCourse(course)}
                style={{
                  backgroundColor: props.selectedCourseIds.includes(course.id)
                    ? "#d5d1fd"
                    : "#d5d1fd",
                  borderColor: "#d5d1fd",
                }}
              >
                {props.selectedCourseIds.includes(course.id)
                  ? "Remove"
                  : "Select"}
              </button>
            </div>
          );
        })}
      </ol>
    </div>
  );
}
